import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flame, Clock, Footprints, HardHat, Shirt, LayoutGrid } from "lucide-react";
import StoreLayout from "../components/StoreLayout";
import ProductCard from "../components/ProductCard";
import RecommendedStrip from "../components/RecommendedStrip";
import { api } from "../lib/api";

const CATEGORIES = [
  { id: "all", label: "All", Icon: LayoutGrid, live: true },
  { id: "clothing", label: "Clothing", Icon: Shirt, live: true },
  { id: "shoes", label: "Shoes", Icon: Footprints, live: false },
  { id: "caps", label: "Caps", Icon: HardHat, live: false },
];

const COLLECTIONS = [
  { id: "all", label: "All" },
  { id: "drop-01", label: "Drop" },
  { id: "limited", label: "Limited" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const collection = params.get("collection") || "all";
  const sort = params.get("sort") || "";
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [size, setSize] = useState("");

  useEffect(() => {
    if (sort === "hot") {
      api.get("/products-hot?limit=20").then((r) => setProducts(r.data || []));
    } else {
      api.get("/products").then((r) => setProducts(r.data));
    }
  }, [sort]);

  // Per-session shuffle: new random order on every new browser session
  const shuffledProducts = useMemo(() => {
    if (!products.length) return products;
    const KEY = "cl_shop_shuffle_v2";
    try {
      const stored = sessionStorage.getItem(KEY);
      if (stored) {
        const ids = JSON.parse(stored);
        const map = Object.fromEntries(products.map((p) => [p.id, p]));
        const ordered = ids.map((id) => map[id]).filter(Boolean);
        const newOnes = products.filter((p) => !ids.includes(p.id));
        return [...ordered, ...newOnes];
      }
    } catch { /* ignore */ }
    // New session → Fisher-Yates shuffle
    const arr = [...products];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    try { sessionStorage.setItem(KEY, JSON.stringify(arr.map((p) => p.id))); } catch { /* ignore */ }
    return arr;
  }, [products]);

  const activeCat = CATEGORIES.find((c) => c.id === category) || CATEGORIES[0];
  const showComingSoon = !activeCat.live;

  const filtered = useMemo(() => {
    if (showComingSoon) return [];
    return shuffledProducts.filter((p) => {
      const pcat = p.category || "clothing";
      if (category !== "all" && pcat !== category) return false;
      // For live categories: only include live-category products
      if (category === "all" && !CATEGORIES.find((c) => c.id === pcat && c.live)) return false;
      if (sort !== "hot" && collection !== "all" && p.collection !== collection) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (size && !p.sizes.includes(size)) return false;
      return true;
    });
  }, [shuffledProducts, collection, search, size, sort, category, showComingSoon]);

  const setCategory = (id) => {
    if (id === "all") setParams({});
    else setParams({ category: id });
  };

  const heading = category === "shoes" ? <>The <span className="text-[#CFF048]">Shoes.</span></>
    : category === "caps" ? <>The <span className="text-[#CFF048]">Caps.</span></>
    : category === "clothing" ? <>The <span className="text-[#CFF048]">Clothing.</span></>
    : sort === "hot" ? <>The <span className="text-[#CFF048]">Hot List.</span></>
    : "The Shop";

  return (
    <StoreLayout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <p className="label-mono text-[#71717A]">// CATALOG {sort === "hot" ? "// HOT SELLING" : ""}</p>
        <h1 className="font-display font-black text-5xl md:text-7xl uppercase tracking-tight mt-3 mb-8">
          {heading}
        </h1>

        {/* CATEGORY TABS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="category-tabs">
          {CATEGORIES.map((c) => {
            const Icon = c.Icon;
            const active = c.id === category;
            return (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`relative border p-4 md:p-5 transition-all flex flex-col items-start text-left ${
                  active ? "border-[#CFF048] bg-[#CFF048]/5" : "border-[#27272A] hover:border-white"
                }`}
                data-testid={`category-tab-${c.id}`}
              >
                <Icon size={22} className={active ? "text-[#CFF048]" : "text-white"} />
                <p className={`font-display font-black text-lg md:text-2xl uppercase mt-2 leading-none ${active ? "text-[#CFF048]" : "text-white"}`}>
                  {c.label}
                </p>
                {c.live ? (
                  <p className="label-mono text-[10px] text-[#71717A] mt-1">
                    // {c.id === "all" ? "EVERY CATEGORY" : "LIVE COLLECTION"}
                  </p>
                ) : (
                  <p className="label-mono text-[10px] text-[#CFF048] mt-1 flex items-center gap-1">
                    <Clock size={10} /> COMING SOON
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* RECOMMENDED FOR YOU — only on the Shop All tab (no active collection/sort filter) */}
        {category === "all" && collection === "all" && sort !== "hot" && !search && !size && (
          <RecommendedStrip />
        )}

        {/* SHARED FILTER ROW — visible on all categories per user request */}
        <div className={`border-y border-[#27272A] py-4 mb-10 flex flex-wrap items-center gap-3 md:gap-6 ${showComingSoon ? "opacity-50" : ""}`}>
          <div className="flex flex-wrap gap-2">
            {COLLECTIONS.map((c) => (
              <button
                key={c.id}
                disabled={showComingSoon}
                onClick={() => setParams(
                  c.id === "all"
                    ? (category === "all" ? {} : { category })
                    : (category === "all" ? { collection: c.id } : { category, collection: c.id })
                )}
                className={`label-mono px-3 py-2 border transition-colors ${
                  sort !== "hot" && collection === c.id ? "bg-[#CFF048] text-black border-[#CFF048]" : "border-[#27272A] hover:border-white"
                } disabled:cursor-not-allowed`}
                data-testid={`filter-collection-${c.id}`}
              >
                {c.label}
              </button>
            ))}
            <button
              disabled={showComingSoon}
              onClick={() => setParams(category === "all" ? { sort: "hot" } : { category, sort: "hot" })}
              className={`label-mono px-3 py-2 border transition-colors flex items-center gap-1.5 ${
                sort === "hot" ? "bg-[#CFF048] text-black border-[#CFF048]" : "border-[#27272A] hover:border-white"
              } disabled:cursor-not-allowed`}
              data-testid="filter-hot"
            >
              <Flame size={13} /> Hot Selling
            </button>
          </div>
          <div className="flex-1" />
          <input
            placeholder="SEARCH..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={showComingSoon}
            className="cult-input max-w-[200px]"
            data-testid="shop-search"
          />
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            disabled={showComingSoon}
            className="cult-input max-w-[120px]"
            data-testid="shop-size-filter"
          >
            <option value="">ALL SIZES</option>
            {["S", "M", "L", "XL", "XXL"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* BODY */}
        {showComingSoon ? (
          <ComingSoon category={activeCat} />
        ) : filtered.length === 0 ? (
          <p className="label-mono text-[#71717A] py-20 text-center">// NO RESULTS FOUND</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((p, i) => (
              <div key={p.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <ProductCard p={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </StoreLayout>
  );
}

function ComingSoon({ category }) {
  const Icon = category.Icon;
  return (
    <section
      className="border-2 border-[#CFF048] bg-gradient-to-b from-[#0A0A0A] to-black p-10 md:p-20 text-center relative overflow-hidden"
      data-testid={`coming-soon-${category.id}`}
    >
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(#CFF048 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="relative">
        <Icon size={60} className="mx-auto text-[#CFF048]" strokeWidth={1.4} />
        <p className="label-mono text-[#CFF048] mt-8 tracking-[0.4em]">// COMING SOON</p>
        <h2 className="font-display font-black text-5xl md:text-7xl uppercase tracking-tight mt-4 leading-none">
          {category.label}<br />
          <span className="text-[#CFF048]">Loading.</span>
        </h2>
        <p className="text-[#A1A1AA] mt-6 max-w-md mx-auto">
          Cult Lyf is expanding into {category.label.toLowerCase()}. Same heavyweight energy, same limited runs, brand-new form factor.
          Join the drop list — first access when it lands.
        </p>
        <a
          href="/#newsletter"
          className="inline-block mt-8 btn-neon"
          data-testid={`notify-${category.id}-btn`}
        >
          NOTIFY ME AT DROP
        </a>
      </div>
    </section>
  );
}
